import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Bot, Server, Globe, Activity, AlertTriangle,
  Plus, Trash2, RefreshCw, ChevronDown, ChevronRight,
  Copy, Check, Eye, EyeOff, Pause, Play, XCircle,
  Cpu, Zap, DollarSign, Clock, ArrowUpDown, Search,
  Network, Lock, Unlock, Skull, Building2, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const STATE_CONFIG = {
  active:       { label: 'Active',      color: 'bg-emerald-500', textColor: 'text-emerald-400', icon: Play },
  idle:         { label: 'Idle',         color: 'bg-slate-400',   textColor: 'text-slate-400',   icon: Pause },
  blocked:      { label: 'Blocked',      color: 'bg-red-500',     textColor: 'text-red-400',     icon: XCircle },
  error:        { label: 'Error',        color: 'bg-orange-500',  textColor: 'text-orange-400',  icon: AlertTriangle },
  hitl_pause:   { label: 'HITL Pause',   color: 'bg-amber-500',   textColor: 'text-amber-400',   icon: Eye },
  deregistered: { label: 'Deregistered', color: 'bg-slate-600',   textColor: 'text-slate-500',   icon: Trash2 },
};

const FRAMEWORK_LABELS = {
  crewai: 'CrewAI',
  langgraph: 'LangGraph',
  autogen: 'AutoGen',
  openai_agents: 'OpenAI Agents',
  anthropic_mcp: 'Anthropic MCP',
  custom: 'Custom',
  unknown: 'Unknown',
};

function StateBadge({ state }) {
  const cfg = STATE_CONFIG[state] || STATE_CONFIG.idle;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.textColor} bg-opacity-10`}
          style={{ backgroundColor: `${cfg.color.replace('bg-', '')}15` }}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.color}`} />
      {cfg.label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sublabel, color = 'text-blue-400' }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg bg-slate-700/50`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <span className="text-sm text-slate-400">{label}</span>
      </div>
      <div className="text-3xl font-bold text-white">{value}</div>
      {sublabel && <div className="text-xs text-slate-500 mt-1">{sublabel}</div>}
    </div>
  );
}

function AgentRow({ agent, onStateChange, onSelect, onDeregister, onKill, onEnforce }) {
  const [expanded, setExpanded] = useState(false);
  const isInternal = agent.origin === 'internal';
  const stale = agent.is_stale || false;

  return (
    <div className="border border-slate-700/50 rounded-lg overflow-hidden mb-2 bg-slate-800/30">
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-800/60 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <button className="text-slate-500 hover:text-slate-300">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        <div className={`p-1.5 rounded-md ${isInternal ? 'bg-blue-500/10' : 'bg-purple-500/10'}`}>
          {isInternal
            ? <Cpu className="w-4 h-4 text-blue-400" />
            : <Globe className="w-4 h-4 text-purple-400" />
          }
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white truncate">{agent.name}</span>
            {stale && isInternal && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-medium">STALE</span>
            )}
          </div>
          <div className="text-xs text-slate-500 truncate">
            {isInternal
              ? `${agent.owner} · ${FRAMEWORK_LABELS[agent.framework] || agent.framework}`
              : `${agent.source_ip || 'Unknown IP'} · ${agent.company || 'Unknown'}`
            }
          </div>
        </div>

        <StateBadge state={agent.state} />

        <span className="text-xs text-slate-500 hidden sm:block w-20 text-right">
          {agent.origin === 'internal' ? agent.department || '—' : `${agent.interaction_count || 0} hits`}
        </span>

        <div className="flex items-center gap-1 ml-2">
          {agent.state !== 'blocked' && agent.state !== 'deregistered' && (
            <button
              className="p-1.5 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition-colors"
              title="Block"
              onClick={(e) => { e.stopPropagation(); onStateChange(agent.agent_id, 'blocked', 'Manual block'); }}
            >
              <Lock className="w-3.5 h-3.5" />
            </button>
          )}
          {agent.state === 'blocked' && (
            <button
              className="p-1.5 rounded hover:bg-emerald-500/20 text-slate-500 hover:text-emerald-400 transition-colors"
              title="Unblock"
              onClick={(e) => { e.stopPropagation(); onStateChange(agent.agent_id, 'active', 'Manual unblock'); }}
            >
              <Unlock className="w-3.5 h-3.5" />
            </button>
          )}
          {agent.state !== 'deregistered' && agent.state !== 'blocked' && (
            <button
              className="p-1.5 rounded hover:bg-red-500/30 text-red-400 hover:text-red-300 transition-colors"
              title="Kill (block + revoke key)"
              onClick={(e) => { e.stopPropagation(); onKill(agent); }}
            >
              <Skull className="w-3.5 h-3.5" />
            </button>
          )}
          {agent.origin === 'external' && agent.source_ip && (
            <button
              className="p-1.5 rounded hover:bg-amber-500/20 text-slate-500 hover:text-amber-400 transition-colors"
              title="Generate enforcement artifact"
              onClick={(e) => { e.stopPropagation(); onEnforce(agent); }}
            >
              <Network className="w-3.5 h-3.5" />
            </button>
          )}
          {agent.state !== 'deregistered' && (
            <button
              className="p-1.5 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition-colors"
              title="Deregister"
              onClick={(e) => { e.stopPropagation(); onDeregister(agent.agent_id); }}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-700/30 bg-slate-800/20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div>
              <span className="text-slate-500">Agent ID</span>
              <p className="text-slate-300 font-mono mt-0.5 truncate">{agent.agent_id}</p>
            </div>
            <div>
              <span className="text-slate-500">Origin</span>
              <p className="text-slate-300 mt-0.5 capitalize">{agent.origin}</p>
            </div>
            {isInternal ? (
              <>
                <div>
                  <span className="text-slate-500">Framework</span>
                  <p className="text-slate-300 mt-0.5">{FRAMEWORK_LABELS[agent.framework] || agent.framework}</p>
                </div>
                <div>
                  <span className="text-slate-500">Last Heartbeat</span>
                  <p className="text-slate-300 mt-0.5">
                    {agent.last_heartbeat ? new Date(agent.last_heartbeat).toLocaleString() : 'Never'}
                  </p>
                </div>
                {agent.tools?.length > 0 && (
                  <div className="col-span-2 md:col-span-4">
                    <span className="text-slate-500">Tools</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {agent.tools.map((t) => (
                        <span key={t} className="px-2 py-0.5 bg-slate-700/50 text-slate-300 rounded text-[10px]">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
                {agent.telemetry_summary && Object.keys(agent.telemetry_summary).length > 0 && (
                  <div className="col-span-2 md:col-span-4 grid grid-cols-3 gap-3 pt-2 border-t border-slate-700/30">
                    <div>
                      <span className="text-slate-500">Events</span>
                      <p className="text-slate-300 font-medium">{agent.telemetry_summary.total_events || 0}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Tokens</span>
                      <p className="text-slate-300 font-medium">{(agent.telemetry_summary.total_tokens || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Cost</span>
                      <p className="text-slate-300 font-medium">${(agent.telemetry_summary.total_cost_usd || 0).toFixed(4)}</p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div>
                  <span className="text-slate-500">User Agent</span>
                  <p className="text-slate-300 mt-0.5 truncate">{agent.user_agent || '—'}</p>
                </div>
                <div>
                  <span className="text-slate-500">rDNS Verified</span>
                  <p className={`mt-0.5 ${agent.rdns_verified ? 'text-emerald-400' : 'text-red-400'}`}>
                    {agent.rdns_verified ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <span className="text-slate-500">First Seen</span>
                  <p className="text-slate-300 mt-0.5">{agent.first_seen ? new Date(agent.first_seen).toLocaleString() : '—'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Last Seen</span>
                  <p className="text-slate-300 mt-0.5">{agent.last_seen ? new Date(agent.last_seen).toLocaleString() : '—'}</p>
                </div>
              </>
            )}
            {agent.description && (
              <div className="col-span-2 md:col-span-4">
                <span className="text-slate-500">Description</span>
                <p className="text-slate-300 mt-0.5">{agent.description}</p>
              </div>
            )}
          </div>

          {agent.state_history?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-700/30">
              <span className="text-xs text-slate-500">State History</span>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {agent.state_history.slice(-6).map((h, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-slate-700/50 text-slate-400">
                    {h.state} — {h.reason || '—'} · {new Date(h.at).toLocaleTimeString()}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RegisterAgentDialog({ open, onOpenChange, onRegistered }) {
  const [name, setName] = useState('');
  const [owner, setOwner] = useState('');
  const [framework, setFramework] = useState('custom');
  const [description, setDescription] = useState('');
  const [department, setDepartment] = useState('');
  const [tools, setTools] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !owner.trim()) return;
    setLoading(true);
    try {
      const res = await api.registerAgent({
        name: name.trim(),
        owner: owner.trim(),
        framework,
        description: description.trim(),
        department: department.trim(),
        tools: tools.split(',').map(t => t.trim()).filter(Boolean),
      });
      setResult(res);
      onRegistered();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(result.api_key);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  const handleClose = () => {
    setName(''); setOwner(''); setFramework('custom'); setDescription('');
    setDepartment(''); setTools(''); setResult(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white">
            {result ? 'Agent Registered' : 'Register Internal Agent'}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {result
              ? 'Save the API key below. It will not be shown again.'
              : 'Register a new managed agent with the control plane.'
            }
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs text-slate-500">Agent ID</label>
              <p className="font-mono text-sm text-slate-300 bg-slate-800 rounded px-3 py-2 mt-1">{result.agent_id}</p>
            </div>
            <div>
              <label className="text-xs text-slate-500">API Key (one-time display)</label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 text-sm text-emerald-400 bg-slate-800 rounded px-3 py-2 break-all">{result.api_key}</code>
                <button onClick={copyKey} className="p-2 rounded hover:bg-slate-700 transition-colors">
                  {keyCopied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
                </button>
              </div>
            </div>
            <Button onClick={handleClose} className="w-full bg-blue-600 hover:bg-blue-700">Done</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Agent Name *</label>
                <Input className="bg-slate-800 border-slate-700 text-white mt-1" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Sales-Research-Bot" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Owner *</label>
                <Input className="bg-slate-800 border-slate-700 text-white mt-1" value={owner} onChange={e => setOwner(e.target.value)} placeholder="e.g. jane@company.com" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Framework</label>
                <Select value={framework} onValueChange={setFramework}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {Object.entries(FRAMEWORK_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k} className="text-slate-200 focus:bg-slate-700 focus:text-white">{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500">Department</label>
                <Input className="bg-slate-800 border-slate-700 text-white mt-1" value={department} onChange={e => setDepartment(e.target.value)} placeholder="e.g. Engineering" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500">Description</label>
              <Input className="bg-slate-800 border-slate-700 text-white mt-1" value={description} onChange={e => setDescription(e.target.value)} placeholder="What does this agent do?" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Tools (comma-separated)</label>
              <Input className="bg-slate-800 border-slate-700 text-white mt-1" value={tools} onChange={e => setTools(e.target.value)} placeholder="e.g. web_search, code_exec, file_read" />
            </div>
            <Button type="submit" disabled={loading || !name.trim() || !owner.trim()} className="w-full bg-blue-600 hover:bg-blue-700">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Register Agent
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AgentsPage() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterOrigin, setFilterOrigin] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [search, setSearch] = useState('');
  const [registerOpen, setRegisterOpen] = useState(false);
  const pollRef = useRef(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const params = {};
      if (filterOrigin !== 'all') params.origin = filterOrigin;
      if (filterState !== 'all') params.state = filterState;
      const data = await api.getAgentsDashboard(params);
      setDashboard(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, [filterOrigin, filterState]);

  useEffect(() => {
    fetchDashboard();
    pollRef.current = setInterval(fetchDashboard, 15000);
    return () => clearInterval(pollRef.current);
  }, [fetchDashboard]);

  const handleStateChange = async (agentId, newState, reason) => {
    try {
      await api.updateAgentState(agentId, newState, reason);
      toast.success(`Agent state updated to ${newState}`);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'State change failed');
    }
  };

  const handleDeregister = async (agentId) => {
    try {
      await api.deregisterAgent(agentId);
      toast.success('Agent deregistered');
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deregister failed');
    }
  };

  const [killTarget, setKillTarget] = useState(null);
  const [enforceTarget, setEnforceTarget] = useState(null);
  const [killReason, setKillReason] = useState('');
  const [enforcing, setEnforcing] = useState(false);
  const [enforceResult, setEnforceResult] = useState(null);

  const handleKill = (agent) => {
    setKillTarget(agent);
    setKillReason('');
  };

  const confirmKill = async () => {
    if (!killTarget) return;
    try {
      const result = await api.killAgent(killTarget.agent_id, {
        reason: killReason || 'Manual kill',
        revoke_key: true,
      });
      toast.success(`Killed ${killTarget.name}. ${result.new_api_key_revoked_notice || ''}`);
      setKillTarget(null);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kill failed');
    }
  };

  const handleEnforce = async (agent) => {
    setEnforceTarget(agent);
    setEnforceResult(null);
  };

  const generateEnforcement = async (target) => {
    if (!enforceTarget) return;
    setEnforcing(true);
    try {
      const res = await api.generateEnforcement(enforceTarget.agent_id, target);
      setEnforceResult(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate enforcement');
    } finally {
      setEnforcing(false);
    }
  };

  const filteredAgents = (dashboard?.agents || []).filter((a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      a.name?.toLowerCase().includes(q) ||
      a.owner?.toLowerCase().includes(q) ||
      a.agent_id?.toLowerCase().includes(q) ||
      a.source_ip?.toLowerCase().includes(q) ||
      a.company?.toLowerCase().includes(q) ||
      a.detected_bot?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white text-lg">AgentCheck</span>
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/" className="text-slate-400 hover:text-white transition-colors text-sm">Site Scan</Link>
            <Link to="/monitor" className="text-slate-400 hover:text-white transition-colors text-sm">Monitor</Link>
            <span className="text-white font-medium text-sm">Agent Control Plane</span>
            <Link to="/agents/detection" className="text-slate-400 hover:text-white transition-colors text-sm">Detection</Link>
            <Link to="/agents/stack-deploy" className="text-slate-400 hover:text-white transition-colors text-sm">Stack Deploy</Link>
            <Link to="/agents/governance" className="text-slate-400 hover:text-white transition-colors text-sm">Governance</Link>
            <Link to="/agents/ai-doctor" className="text-slate-400 hover:text-white transition-colors text-sm">AI Doctor</Link>
            <Link to="/agents/ai-debt" className="text-slate-400 hover:text-white transition-colors text-sm">AI Debt</Link>
            <Link to="/docs" className="text-slate-400 hover:text-white transition-colors text-sm">Docs</Link>
            <Link to="/orgs" className="text-slate-400 hover:text-white transition-colors text-sm">Orgs</Link>
            <Link to="/docs/api" className="text-slate-400 hover:text-white transition-colors text-sm">API Docs</Link>
          </nav>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-xl bg-blue-500/10 border border-blue-500/20">
                <Network className="w-6 h-6 text-blue-400" />
              </div>
              <h1 className="text-2xl font-bold text-white">Agent Control Plane</h1>
            </div>
            <p className="text-slate-400 text-sm">
              Unified visibility into internal managed agents and external AI agents crossing your network.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
              onClick={fetchDashboard}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setRegisterOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Register Agent
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <StatCard icon={Bot} label="Total Agents" value={dashboard.total_agents} color="text-blue-400" />
            <StatCard icon={Server} label="Internal" value={dashboard.internal_count} sublabel="Managed agents" color="text-sky-400" />
            <StatCard icon={Globe} label="External" value={dashboard.external_count} sublabel="Perimeter agents" color="text-purple-400" />
            <StatCard
              icon={Activity}
              label="Active"
              value={dashboard.state_breakdown?.active || 0}
              sublabel={`${dashboard.stale_agents || 0} stale`}
              color="text-emerald-400"
            />
            <StatCard
              icon={AlertTriangle}
              label="Blocked / Error"
              value={(dashboard.state_breakdown?.blocked || 0) + (dashboard.state_breakdown?.error || 0)}
              color="text-red-400"
            />
          </div>
        )}

        {/* Breakdown Badges */}
        {dashboard && (
          <div className="flex flex-wrap gap-6 mb-8">
            {Object.keys(dashboard.framework_breakdown || {}).length > 0 && (
              <div>
                <span className="text-xs text-slate-500 uppercase tracking-wider">Frameworks</span>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {Object.entries(dashboard.framework_breakdown).map(([f, c]) => (
                    <span key={f} className="px-2.5 py-1 rounded-full bg-sky-500/10 text-sky-400 text-xs font-medium">
                      {FRAMEWORK_LABELS[f] || f}: {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {Object.keys(dashboard.company_breakdown || {}).length > 0 && (
              <div>
                <span className="text-xs text-slate-500 uppercase tracking-wider">External Companies</span>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {Object.entries(dashboard.company_breakdown).map(([co, c]) => (
                    <span key={co} className="px-2.5 py-1 rounded-full bg-purple-500/10 text-purple-400 text-xs font-medium">
                      {co}: {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              className="bg-slate-800 border-slate-700 text-white pl-9"
              placeholder="Search agents..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={filterOrigin} onValueChange={setFilterOrigin}>
            <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white">
              <SelectValue placeholder="Origin" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all" className="text-slate-200 focus:bg-slate-700 focus:text-white">All Origins</SelectItem>
              <SelectItem value="internal" className="text-slate-200 focus:bg-slate-700 focus:text-white">Internal</SelectItem>
              <SelectItem value="external" className="text-slate-200 focus:bg-slate-700 focus:text-white">External</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterState} onValueChange={setFilterState}>
            <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white">
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all" className="text-slate-200 focus:bg-slate-700 focus:text-white">All States</SelectItem>
              {Object.entries(STATE_CONFIG).map(([k, v]) => (
                <SelectItem key={k} value={k} className="text-slate-200 focus:bg-slate-700 focus:text-white">{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-500 ml-auto">
            {filteredAgents.length} agent{filteredAgents.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Agent List */}
        {loading && !dashboard && (
          <div className="text-center py-20">
            <RefreshCw className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Loading agent registry...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-20">
            <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-4" />
            <p className="text-red-400">{error}</p>
            <Button variant="outline" className="mt-4 border-slate-600 text-slate-300" onClick={fetchDashboard}>
              Retry
            </Button>
          </div>
        )}

        {dashboard && filteredAgents.length === 0 && (
          <div className="text-center py-20 border border-dashed border-slate-700 rounded-xl">
            <Bot className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 mb-2">No agents registered yet</p>
            <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
              Register internal agents via the button above or the SDK. External agents are auto-detected from network activity.
            </p>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setRegisterOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Register Your First Agent
            </Button>
          </div>
        )}

        {filteredAgents.length > 0 && (
          <div>
            {filteredAgents.map((agent) => (
              <AgentRow
                key={agent.agent_id}
                agent={agent}
                onStateChange={handleStateChange}
                onDeregister={handleDeregister}
                onKill={handleKill}
                onEnforce={handleEnforce}
              />
            ))}
          </div>
        )}

        {/* SDK Quick Start */}
        <div className="mt-12 border border-slate-700/50 rounded-xl p-6 bg-slate-800/30">
          <h3 className="text-lg font-semibold text-white mb-4">SDK Quick Start</h3>
          <p className="text-sm text-slate-400 mb-4">
            Register and instrument your agents programmatically using the REST API or the Python SDK.
          </p>
          <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
            <pre className="text-sm text-slate-300 font-mono whitespace-pre">{`import requests

API = "https://your-backend/api/v1"

# 1. Register agent
resp = requests.post(f"{API}/agents/register", json={
    "name": "Sales-Research-Bot",
    "owner": "jane@company.com",
    "framework": "crewai",
    "department": "Sales",
    "tools": ["web_search", "crm_lookup"]
})
agent_id = resp.json()["agent_id"]
api_key  = resp.json()["api_key"]   # store securely

# 2. Send heartbeat (every 60s)
requests.post(f"{API}/agents/heartbeat", json={
    "agent_id": agent_id,
    "api_key": api_key,
    "state": "active",
    "metrics": {"tasks_completed": 12}
})

# 3. Report telemetry
requests.post(f"{API}/agents/telemetry", json={
    "agent_id": agent_id,
    "api_key": api_key,
    "event_type": "tool_call",
    "tool_name": "web_search",
    "tokens": 1500,
    "cost_usd": 0.002,
    "message": "Searched competitor pricing"
})`}</pre>
          </div>
        </div>
      </div>

      <RegisterAgentDialog
        open={registerOpen}
        onOpenChange={setRegisterOpen}
        onRegistered={fetchDashboard}
      />

      {/* Kill Dialog */}
      <Dialog open={!!killTarget} onOpenChange={(open) => !open && setKillTarget(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Skull className="w-5 h-5 text-red-400" />
              Kill Agent
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {killTarget?.origin === 'internal'
                ? 'This will block the agent and revoke its API key. The agent will be unable to recover.'
                : 'This will block the external agent in our registry. Use the enforcement artifacts to enforce at your network edge.'}
            </DialogDescription>
          </DialogHeader>
          {killTarget && (
            <div className="space-y-4 pt-2">
              <div className="bg-slate-800 rounded-lg p-3">
                <p className="text-sm text-white font-medium">{killTarget.name}</p>
                <p className="text-xs text-slate-500 font-mono mt-1">{killTarget.agent_id}</p>
              </div>
              <div>
                <label className="text-xs text-slate-500">Reason (optional, recorded in audit log)</label>
                <Input
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  placeholder="e.g. suspicious data exfiltration pattern"
                  value={killReason}
                  onChange={e => setKillReason(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                  onClick={() => setKillTarget(null)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={confirmKill}
                >
                  <Skull className="w-4 h-4 mr-2" />
                  Confirm Kill
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Enforcement Dialog */}
      <Dialog open={!!enforceTarget} onOpenChange={(open) => !open && setEnforceTarget(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Network className="w-5 h-5 text-amber-400" />
              Generate Enforcement Artifact
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Generate firewall, WAF, or proxy rules to block this agent at your network edge.
            </DialogDescription>
          </DialogHeader>
          {enforceTarget && (
            <div className="space-y-4 pt-2">
              <div className="bg-slate-800 rounded-lg p-3 text-sm">
                <p className="text-white font-medium">{enforceTarget.name}</p>
                <p className="text-slate-500 text-xs font-mono mt-1">
                  IP: {enforceTarget.source_ip || '—'} · UA: {(enforceTarget.user_agent || '—').substring(0, 60)}
                </p>
              </div>
              <div className="grid grid-cols-5 gap-2">
                {['iptables', 'nftables', 'cloudflare', 'aws_waf', 'nginx'].map(t => (
                  <Button
                    key={t}
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:bg-slate-800 text-xs"
                    onClick={() => generateEnforcement(t)}
                    disabled={enforcing}
                  >
                    {t}
                  </Button>
                ))}
              </div>
              {enforceResult && (
                <div className="bg-slate-950 rounded-lg p-4 max-h-80 overflow-y-auto">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-500 uppercase">{enforceResult.target}</span>
                    <button
                      onClick={() => {
                        const content = enforceResult.artifact?.rule || enforceResult.artifact?.config || JSON.stringify(enforceResult.artifact, null, 2);
                        navigator.clipboard.writeText(content);
                        toast.success('Copied to clipboard');
                      }}
                      className="text-slate-400 hover:text-white"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {enforceResult.artifact?.error ? (
                    <p className="text-red-400 text-sm">{enforceResult.artifact.error}</p>
                  ) : (
                    <pre className="text-xs text-emerald-400 font-mono whitespace-pre-wrap break-all">
                      {enforceResult.artifact?.rule
                        || enforceResult.artifact?.config
                        || JSON.stringify(enforceResult.artifact, null, 2)}
                    </pre>
                  )}
                  {enforceResult.artifact?.instructions && (
                    <p className="text-slate-400 text-xs mt-3 border-t border-slate-700 pt-3">
                      {enforceResult.artifact.instructions}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
